import { useState, useEffect } from 'react';

/**
 * Hook customizado para gerenciar zoom e cálculos de largura de colunas de tabelas
 * 
 * @param {Object} columnWidths - Configuração de larguras das colunas
 * @returns {Object} - Objeto com funções e estado do zoom
 */
const useTableZoom = (columnWidths) => {
	const [zoomLevel, setZoomLevel] = useState(1);

	// Detectar zoom da tela
	useEffect(() => {
		const updateZoom = () => {
			const newZoom = window.devicePixelRatio || 1;
			if (Math.abs(newZoom - zoomLevel) > 0.1) {
				setZoomLevel(newZoom);
			}
		};

		updateZoom();
		const zoomInterval = setInterval(updateZoom, 1000);
		window.addEventListener('resize', updateZoom);

		return () => {
			clearInterval(zoomInterval);
			window.removeEventListener('resize', updateZoom);
		};
	}, [zoomLevel]);

	// Detecta automaticamente as colunas principais (sem max definido)
	const getMainColumns = () => {
		return Object.keys(columnWidths).filter(key => 
			columnWidths[key].max === undefined
		);
	};

	// Função para ajustar largura das colunas principais baseado no zoom
	const getMainColumnWidth = (columnKey = null) => {
		const mainColumns = getMainColumns();
		
		// Se foi passado um columnKey específico, verifica se é uma coluna principal
		if (columnKey && !mainColumns.includes(columnKey)) {
			console.warn(`⚠️ Coluna '${columnKey}' não é uma coluna principal. Usando getOtherColumnsWidth() em vez disso.`);
			return getOtherColumnsWidth()[columnKey];
		}
		
		// Calcula o espaço ocupado pelas colunas fixas (com max definido)
		const otherColumnsWidth = getOtherColumnsWidth();
		const fixedColumnsWidth = Object.keys(otherColumnsWidth)
			.filter(key => !mainColumns.includes(key))
			.reduce((sum, key) => sum + otherColumnsWidth[key], 0);
		
		// Ajusta a largura disponível baseada no zoom para prevenir scroll
		let availableWidthPercentage = 0.80; // 80% padrão
		
		// Ajuste específico para diferentes níveis de zoom
		if (zoomLevel >= 1.5) {
			availableWidthPercentage = 0.75; // 75% para zoom alto
		} else if (zoomLevel >= 1.25) {
			availableWidthPercentage = 0.76; // 76% para zoom 125%
		} else if (zoomLevel >= 1.2) {
			availableWidthPercentage = 0.78; // 78% para zoom 120%
		}
		
		const availableWidth = Math.min(window.innerWidth * availableWidthPercentage, 1200); // Limite máximo de 1200px
		
		// Calcula o espaço restante para as colunas principais
		const remainingSpacePixels = availableWidth - fixedColumnsWidth;
		
		// Distribui o espaço restante proporcionalmente entre as colunas principais
		const spacePerMainColumn = Math.max(
			remainingSpacePixels / mainColumns.length,
			0
		);
		
		// Se foi solicitada uma coluna específica, calcula sua largura
		if (columnKey) {
			const minWidth = columnWidths[columnKey].min;
			const calculatedWidth = Math.max(spacePerMainColumn, minWidth);

			return calculatedWidth;
		}
		
		// Se não foi especificada coluna, retorna a largura padrão (para compatibilidade)
		const defaultMainColumn = mainColumns[0];
		if (defaultMainColumn) {
			const minWidth = columnWidths[defaultMainColumn].min;
			const calculatedWidth = Math.max(spacePerMainColumn, minWidth);
			
			// Verificação de segurança: se a tabela ficou muito pequena, usar largura mínima total
			const totalMinWidth = Object.values(columnWidths).reduce((sum, col) => sum + col.min, 0);
			if (availableWidth < totalMinWidth) {
				// Se a largura disponível é menor que o mínimo necessário, usar menos da tela
				const emergencyWidth = window.innerWidth * 0.80; // Reduzido de 0.82 para 0.80
				const emergencyRemaining = emergencyWidth - fixedColumnsWidth;
				const emergencyPerColumn = Math.max(emergencyRemaining / mainColumns.length, minWidth);
				
				return emergencyPerColumn;
			}
			
			return calculatedWidth;
		}
		
		console.warn(`⚠️ Nenhuma coluna principal encontrada. Verifique a configuração de columnWidths.`);
		return 200; // Fallback
	};

	// Função para ajustar larguras das outras colunas baseado no zoom
	const getOtherColumnsWidth = () => {
		// Função auxiliar para aplicar min/max (em pixels)
		const applyConstraints = (value, columnConfig) => {
			let result = Math.max(value, columnConfig.min); // Garante mínimo
			if (columnConfig.max !== undefined) {
				result = Math.min(result, columnConfig.max); // Aplica máximo se definido
			}
			return result;
		};

		// Calcula fator de ajuste baseado no zoom
		const getZoomFactor = () => {
			if (zoomLevel >= 1.5) return 1.4; // 40% de aumento
			if (zoomLevel >= 1.3) return 1.3; // 30% de aumento
			if (zoomLevel >= 1.25) return 1.2; // 20% de aumento (reduzido para ser mais conservador)
			if (zoomLevel >= 1.2) return 1.15; // 15% de aumento
			return 1.0; // Sem ajuste
		};

		const zoomFactor = getZoomFactor();

		// Calcula larguras dinâmicas baseadas no zoom (em pixels)
		const calculateDynamicWidth = (columnConfig) => {
			const baseWidth = columnConfig.min;
			const maxPossible = columnConfig.max || baseWidth * 2; // Se não tem max, permite dobrar
			const zoomedWidth = baseWidth * zoomFactor;
			
			return Math.min(zoomedWidth, maxPossible);
		};

		// Aplica cálculo dinâmico para cada coluna (retorna pixels)
		const widths = {};
		Object.keys(columnWidths).forEach(key => {
			widths[key] = applyConstraints(calculateDynamicWidth(columnWidths[key]), columnWidths[key]);
		});

		return widths;
	};

	// Função para obter largura de uma coluna específica (principal ou não)
	const getColumnWidth = (columnKey) => {
		const mainColumns = getMainColumns();
		
		if (mainColumns.includes(columnKey)) {
			return getMainColumnWidth(columnKey);
		} else {
			return getOtherColumnsWidth()[columnKey];
		}
	};

	return {
		zoomLevel,
		getMainColumnWidth,
		getOtherColumnsWidth,
		getColumnWidth, // Função unificada para obter largura de qualquer coluna
		getMainColumns, // Função para obter lista de colunas principais
	};
};

export default useTableZoom; 